import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_factura-mexico-app/artifacts/u9456sea_Logo%20CIA.jpg";

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="terms-page">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="CIA Factura℗" className="h-10 w-auto" />
              <span className="font-bold text-xl text-slate-900">CIA Factura℗</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-slate-600">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver al inicio
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="container mx-auto px-6 md:px-12 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm p-8 md:p-12">
          <h1 className="text-3xl font-bold text-slate-900 mb-8">Términos y Condiciones de Uso</h1>
          
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 mb-6">
              <strong>Última actualización:</strong> {new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            <p className="text-slate-600 mb-6">
              Bienvenido a CIA Factura℗. Al acceder y utilizar nuestros servicios, usted acepta estar sujeto a los siguientes términos y condiciones. Por favor, léalos cuidadosamente antes de utilizar la plataforma.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">1. Definiciones</h2>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>"CIA Factura℗"</strong> o <strong>"la Plataforma"</strong>: Sistema de timbrado de Comprobantes Fiscales Digitales por Internet (CFDI) operado a través de ciafactura.com</li>
              <li><strong>"Usuario"</strong>: Persona física o moral que se registra y utiliza los servicios de la Plataforma</li>
              <li><strong>"CFDI"</strong>: Comprobante Fiscal Digital por Internet, según lo define el Servicio de Administración Tributaria (SAT)</li>
              <li><strong>"Timbrado"</strong>: Proceso de certificación de un CFDI a través de un Proveedor Autorizado de Certificación (PAC)</li>
              <li><strong>"Suscripción"</strong>: Plan de servicio contratado por el Usuario</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">2. Descripción del Servicio</h2>
            <p className="text-slate-600 mb-4">
              CIA Factura℗ es una plataforma que permite a los usuarios:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Generar y timbrar CFDI (facturas de ingreso, notas de crédito, complementos de pago)</li>
              <li>Administrar catálogos de clientes</li>
              <li>Emitir CFDI a través de múltiples canales (web, WhatsApp)</li>
              <li>Generar reportes de documentos fiscales</li>
              <li>Descargar archivos PDF y XML de sus comprobantes</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">3. Registro y Cuenta de Usuario</h2>
            <p className="text-slate-600 mb-4">
              Para utilizar los servicios de CIA Factura℗, el Usuario debe:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Proporcionar información veraz, completa y actualizada durante el registro</li>
              <li>Mantener la confidencialidad de sus credenciales de acceso</li>
              <li>Notificar inmediatamente cualquier uso no autorizado de su cuenta</li>
              <li>Ser mayor de 18 años o tener capacidad legal para contratar</li>
              <li>Contar con un RFC válido para la emisión de CFDI</li>
            </ul>
            <p className="text-slate-600 mb-4">
              <strong>Un usuario solo puede tener un RFC asociado a su cuenta.</strong> Para facturar con múltiples RFC, deberá contratar licencias adicionales.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">4. Obligaciones del Usuario</h2>
            <p className="text-slate-600 mb-4">El Usuario se compromete a:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Utilizar la Plataforma únicamente para fines lícitos y conforme a la legislación fiscal mexicana</li>
              <li>Proporcionar datos fiscales correctos y actualizados</li>
              <li>Resguardar adecuadamente sus Certificados de Sello Digital (CSD)</li>
              <li>No compartir sus credenciales de acceso con terceros</li>
              <li>No utilizar la Plataforma para emitir CFDI fraudulentos o apócrifos</li>
              <li>Cumplir con todas las disposiciones fiscales aplicables</li>
              <li>Pagar oportunamente las suscripciones contratadas</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">5. Planes y Suscripciones</h2>
            <p className="text-slate-600 mb-4">
              CIA Factura℗ ofrece diferentes planes de suscripción con características y precios específicos. Las condiciones de cada plan son:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Los precios publicados incluyen IVA</li>
              <li>El cobro se realiza de forma mensual y anticipada</li>
              <li>Los CFDI no utilizados en un período no se acumulan para el siguiente</li>
              <li>El Usuario puede cambiar de plan en cualquier momento</li>
              <li>La cancelación de suscripción no genera reembolsos por el período en curso</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">6. Proceso de Timbrado</h2>
            <p className="text-slate-600 mb-4">
              El timbrado de CFDI se realiza a través de un Proveedor Autorizado de Certificación (PAC) registrado ante el SAT. El Usuario reconoce que:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Los CFDI timbrados tienen validez fiscal plena ante el SAT</li>
              <li>El Usuario es responsable de la veracidad de los datos contenidos en sus CFDI</li>
              <li>Los tiempos de timbrado pueden variar según la disponibilidad del PAC y el SAT</li>
              <li>La Plataforma no es responsable por errores en los datos proporcionados por el Usuario</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">7. Cancelación de CFDI</h2>
            <p className="text-slate-600 mb-4">
              La cancelación de CFDI está sujeta a las disposiciones vigentes del SAT:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Los CFDI pueden requerir aceptación del receptor para su cancelación</li>
              <li>Existen plazos máximos para cancelar CFDI según la normativa fiscal</li>
              <li>La cancelación de un CFDI no genera reembolso del timbrado consumido</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">8. Servicio de WhatsApp</h2>
            <p className="text-slate-600 mb-4">
              El servicio de timbrado por WhatsApp está sujeto a las siguientes condiciones:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Disponible únicamente para usuarios con suscripción activa que incluya esta funcionalidad</li>
              <li>El Usuario es responsable de verificar los datos antes de confirmar el timbrado</li>
              <li>Se envía una prefactura para confirmación antes de timbrar</li>
              <li>Las conversaciones pueden ser procesadas por sistemas de inteligencia artificial</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">9. Propiedad Intelectual</h2>
            <p className="text-slate-600 mb-4">
              Todos los derechos de propiedad intelectual sobre la Plataforma, incluyendo pero no limitado a software, diseño, logotipos y contenido, son propiedad de CIA Factura℗ o sus licenciantes. El Usuario no adquiere ningún derecho de propiedad intelectual por el uso de la Plataforma.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">10. Limitación de Responsabilidad</h2>
            <p className="text-slate-600 mb-4">CIA Factura℗ no será responsable por:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Errores en los datos proporcionados por el Usuario</li>
              <li>Interrupciones del servicio por causas ajenas a su control</li>
              <li>Daños indirectos, incidentales o consecuentes</li>
              <li>Problemas derivados del incumplimiento de obligaciones fiscales del Usuario</li>
              <li>Fallas en servicios de terceros (PAC, pasarelas de pago, servicios de mensajería)</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">11. Modificaciones a los Términos</h2>
            <p className="text-slate-600 mb-4">
              CIA Factura℗ se reserva el derecho de modificar estos términos y condiciones en cualquier momento. Los cambios serán notificados a través de la Plataforma o por correo electrónico. El uso continuado del servicio después de la notificación constituye aceptación de los nuevos términos.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">12. Terminación</h2>
            <p className="text-slate-600 mb-4">
              CIA Factura℗ puede suspender o terminar el acceso del Usuario a la Plataforma en caso de:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Incumplimiento de estos términos y condiciones</li>
              <li>Uso fraudulento o ilegal de la Plataforma</li>
              <li>Falta de pago de la suscripción</li>
              <li>Solicitud de autoridades competentes</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">13. Ley Aplicable y Jurisdicción</h2>
            <p className="text-slate-600 mb-4">
              Estos términos y condiciones se rigen por las leyes de los Estados Unidos Mexicanos. Cualquier controversia será sometida a los tribunales competentes de la Ciudad de México, renunciando el Usuario a cualquier otro fuero que pudiera corresponderle.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">14. Contacto</h2>
            <p className="text-slate-600 mb-4">
              Para cualquier duda o aclaración sobre estos términos y condiciones, el Usuario puede contactarnos a través de los medios indicados en el panel de administración de CIA Factura℗.
            </p>

            <div className="mt-12 p-6 bg-slate-50 rounded-lg">
              <p className="text-slate-600 text-sm">
                <strong>CIA Factura℗</strong> - Sistema de Timbrado de CFDI<br />
                Dominio: ciafactura.com<br />
                Al utilizar nuestros servicios, usted acepta estos términos y condiciones en su totalidad.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
